<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title" class="mc-page-three" />
      <p>{{ $t('driverScore.text.manageParameter') }}</p>
      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end d-flex">
          <McButton
            variant="secondary"
            icon="icon-outline_arrow-left"
            :text="$t('global.back')"
            @click="backConfirmation()"
          />
          <McButton
            variant="secondary"
            icon="icon-fill_refresh"
            :text="$t('global.action.resetConfig')"
            class="mx-2"
            :disabled="isSubmit"
            @click="resetConfig"
          />
          <McButton
            variant="secondary"
            icon="icon-outline_close"
            :text="$t('global.action.cancel')"
            class="me-2"
            :disabled="isSubmit || !hasChanged"
            @click="cancelConfirmation()"
          />
          <McButton
            icon="icon-outline_save"
            :text="$t('global.save')"
            :disabled="isSubmit || !hasChanged"
            @click="updateData"
          />
        </div>
      </div>
    </div>

    <b-card>
      <FormulateForm ref="formRef" v-model="form">
        <p class="font-size-16">
          {{ $t('driverScore.object.driverScoreProvision') }}
        </p>

        <b-skeleton-wrapper :loading="isLoading">
          <template #loading>
            <b-row class="my-4">
              <b-col md="3">
                <b-skeleton width="100%" height="50px" />
              </b-col>
              <b-col md="3">
                <b-skeleton width="100%" height="50px" />
              </b-col>
            </b-row>
          </template>
          <b-row>
            <b-col md="3">
              <FormulateInput
                suffix
                type="text"
                name="minDistance"
                :placeholder="`${$t('global.form.enter')} ${$t(
                  'driverScore.field.minimumDistance'
                )}`"
                validation-behavior="blur"
                error-behavior="blur"
                :validation="[
                  ['bail'],
                  ['required'],
                  ['number'],
                  ['min', form.minDistance],
                  ['min', 0],
                ]"
                :validation-messages="{
                  min:
                    `${$t('driverScore.field.minimumDistance')} ${$t('global.validation.min')}` +
                    ' 0',
                }"
              >
                <template #label>
                  <div class="d-flex justify-content-between align-items-center">
                    <label>{{ $t('driverScore.field.minimumDistance') }}</label>
                    <span
                      v-b-tooltip.topleft
                      :title="`${$t('driverScore.tooltip.minimumDistance')}`"
                    >
                      <i class="icon-outline_info"></i>
                    </span>
                  </div>
                </template>
                <template #suffix>
                  <div class="formulate-input-suffix">
                    <span>{{ 'Km' }}</span>
                  </div>
                </template>
              </FormulateInput>
            </b-col>
            <b-col md="3">
              <FormulateInput
                type="text"
                name="percentageThreshold"
                suffix
                :placeholder="`${$t('global.form.enter')} ${$t('driverScore.object.alertLimit')}`"
                validation-behavior="blur"
                error-behavior="blur"
                :validation="[
                  ['bail'],
                  ['required'],
                  ['number'],
                  ['min', form.percentageThreshold],
                  ['min', 0],
                  ['max', form.percentageThreshold],
                  ['max', 100],
                ]"
                :validation-messages="{
                  min:
                    `${$t('driverScore.object.alertLimit')} ${$t('global.validation.min')}` + ' 0',
                  max:
                    `${$t('driverScore.object.alertLimit')} ${$t('global.validation.max')}` +
                    ' 100',
                }"
              >
                <template #label>
                  <div class="d-flex justify-content-between align-items-center">
                    <label>{{ $t('driverScore.object.alertLimit') }}</label>
                    <span v-b-tooltip.topleft :title="`${$t('driverScore.tooltip.warningLimit')}`">
                      <i class="icon-outline_info"></i>
                    </span>
                  </div>
                </template>
                <template #suffix>
                  <div class="formulate-input-suffix">
                    <span>%</span>
                  </div>
                </template>
              </FormulateInput>
            </b-col>
          </b-row>
        </b-skeleton-wrapper>
        <div class="d-flex justify-content-between mt-5">
          <p class="font-size-16">
            {{ $t('driverScore.object.parameterPercentage') }}
          </p>
          <b-skeleton-wrapper :loading="isLoading">
            <template #loading>
              <b-skeleton width="200px" height="30px" />
            </template>
            <div>
              {{ $t('global.totalPercentage') }} :
              <span :class="totalPercentage == 100 ? 'green-500' : 'red-500'">
                {{ totalPercentage }}%
              </span>
              /
              <span class="green-500">100%</span>
            </div>
          </b-skeleton-wrapper>
        </div>
        <div class="accordion mc-accordion" role="tablist">
          <div class="mc-accordion-item">
            <div
              v-b-toggle.accordion-1
              header-tag="header"
              role="tab"
              class="mc-accordion__header d-flex justify-content-between"
              block
            >
              <span class="font-size-14">{{ $t('global.alert.gpsAlert') }}</span>
              <i class="mc-fa icon-outline_chevron-down"></i>
            </div>
            <b-collapse id="accordion-1" visible role="tabpanel">
              <div class="mc-accordion__body">
                <b-skeleton-wrapper :loading="isLoading">
                  <template #loading>
                    <b-row class="my-3">
                      <b-col md="3">
                        <b-skeleton width="100%" height="50px" />
                      </b-col>
                      <b-col md="3">
                        <b-skeleton width="100%" height="50px" />
                      </b-col>
                      <b-col md="3">
                        <b-skeleton width="100%" height="50px" />
                      </b-col>
                      <b-col md="3">
                        <b-skeleton width="100%" height="50px" />
                      </b-col>
                    </b-row>
                    <b-row class="my-3">
                      <b-col md="3">
                        <b-skeleton width="100%" height="50px" />
                      </b-col>
                      <b-col md="3">
                        <b-skeleton width="100%" height="50px" />
                      </b-col>
                    </b-row>
                  </template>
                  <b-row>
                    <b-col md="3">
                      <FormulateInput
                        type="text"
                        name="costHarshAccel"
                        suffix
                        :label="$t('global.alert.suddenAcceleration')"
                        :placeholder="`${$t('global.form.enter')} ${$t(
                          'global.alert.suddenAcceleration'
                        )}`"
                        validation-behavior="blur"
                        error-behavior="blur"
                        :validation="[
                          ['required'],
                          ['number'],
                          ['min', form.costHarshAccel],
                          ['min', 0],
                          ['max', form.costHarshAccel],
                          ['max', 100],
                        ]"
                        :validation-messages="{
                          min:
                            ` ${$t('global.alert.suddenAcceleration')} ${$t(
                              'global.validation.min'
                            )}` + ' 0',
                          max:
                            ` ${$t('global.alert.suddenAcceleration')} ${$t(
                              'global.validation.max'
                            )}` + ' 100',
                        }"
                      >
                        <template #suffix>
                          <div class="formulate-input-suffix">
                            <span>%</span>
                          </div>
                        </template>
                      </FormulateInput>
                    </b-col>
                    <b-col md="3">
                      <FormulateInput
                        suffix
                        type="text"
                        name="costHarshBrake"
                        :label="$t('global.alert.suddenDeceleration')"
                        :placeholder="`${$t('global.form.enter')} ${$t(
                          'global.alert.suddenDeceleration'
                        )}`"
                        validation-behavior="blur"
                        error-behavior="blur"
                        :validation="[
                          ['required'],
                          ['number'],
                          ['min', form.costHarshBrake],
                          ['min', 0],
                          ['max', form.costHarshBrake],
                          ['max', 100],
                        ]"
                        :validation-messages="{
                          min:
                            `${$t('global.alert.suddenDeceleration')} ${$t(
                              'global.validation.min'
                            )}` + ' 0',
                          max:
                            `${$t('global.alert.suddenDeceleration')} ${$t(
                              'global.validation.max'
                            )}` + ' 100',
                        }"
                      >
                        <template #suffix>
                          <div class="formulate-input-suffix">
                            <span>%</span>
                          </div>
                        </template>
                      </FormulateInput>
                    </b-col>
                    <b-col md="3">
                      <FormulateInput
                        type="text"
                        name="costSharpturn"
                        :label="$t('global.alert.sharpTurn')"
                        suffix
                        :placeholder="`${$t('global.form.enter')} ${$t('global.alert.sharpTurn')}`"
                        validation-behavior="blur"
                        error-behavior="blur"
                        :validation="[
                          ['required'],
                          ['number'],
                          ['min', form.costSharpturn],
                          ['min', 0],
                          ['max', form.costSharpturn],
                          ['max', 100],
                        ]"
                        :validation-messages="{
                          min:
                            `${$t('global.alert.sharpTurn')} ${$t('global.validation.min')}` + ' 0',
                          max:
                            `${$t('global.alert.sharpTurn')} ${$t('global.validation.max')}` +
                            ' 100',
                        }"
                      >
                        <template #suffix>
                          <div class="formulate-input-suffix">
                            <span>%</span>
                          </div>
                        </template>
                      </FormulateInput>
                    </b-col>
                    <b-col md="3">
                      <FormulateInput
                        type="text"
                        name="costOverspeed"
                        suffix
                        :label="$t('global.alert.overspeed')"
                        :placeholder="`${$t('global.form.enter')} ${$t('global.alert.overspeed')}`"
                        validation-behavior="blur"
                        error-behavior="blur"
                        :validation="[
                          ['required'],
                          ['number'],
                          ['min', form.costOverspeed],
                          ['min', 0],
                          ['max', form.costOverspeed],
                          ['max', 100],
                        ]"
                        :validation-messages="{
                          min:
                            `${$t('global.alert.overspeed')} ${$t('global.validation.min')}` + ' 0',
                          max:
                            `${$t('global.alert.overspeed')} ${$t('global.validation.max')}` +
                            ' 100',
                        }"
                      >
                        <template #suffix>
                          <div class="formulate-input-suffix">
                            <span>%</span>
                          </div>
                        </template>
                      </FormulateInput>
                    </b-col>
                    <b-col md="3" class="mt-2">
                      <FormulateInput
                        type="text"
                        name="costIdleExceeded"
                        :label="$t('global.alert.idleTime')"
                        suffix
                        :placeholder="`${$t('global.form.enter')} ${$t('global.alert.idleTime')}`"
                        validation-behavior="blur"
                        error-behavior="blur"
                        :validation="[
                          ['required'],
                          ['number'],
                          ['min', form.costIdleExceeded],
                          ['min', 0],
                          ['max', form.costIdleExceeded],
                          ['max', 100],
                        ]"
                        :validation-messages="{
                          min:
                            `${$t('global.alert.idleTime')} ${$t('global.validation.min')}` + ' 0',
                          max:
                            `${$t('global.alert.idleTime')} ${$t('global.validation.max')}` +
                            ' 100',
                        }"
                      >
                        <template #suffix>
                          <div class="formulate-input-suffix">
                            <span>%</span>
                          </div>
                        </template>
                      </FormulateInput>
                    </b-col>
                    <b-col md="3" class="mt-2">
                      <FormulateInput
                        type="text"
                        name="costParkExceeded"
                        :label="$t('global.alert.parkTime')"
                        suffix
                        :placeholder="`${$t('global.form.enter')} ${$t('global.alert.parkTime')}`"
                        validation-behavior="blur"
                        error-behavior="blur"
                        :validation="[
                          ['required'],
                          ['number'],
                          ['min', form.costParkExceeded],
                          ['min', 0],
                          ['max', form.costParkExceeded],
                          ['max', 100],
                        ]"
                        :validation-messages="{
                          min:
                            `${$t('global.alert.parkTime')} ${$t('global.validation.min')}` + ' 0',
                          max:
                            `${$t('global.alert.parkTime')} ${$t('global.validation.max')}` +
                            ' 100',
                        }"
                      >
                        <template #suffix>
                          <div class="formulate-input-suffix">
                            <span>%</span>
                          </div>
                        </template>
                      </FormulateInput>
                    </b-col>
                  </b-row>
                </b-skeleton-wrapper>
              </div>
            </b-collapse>
          </div>
          <div class="mc-accordion-item">
            <div
              v-b-toggle.accordion-2
              header-tag="header"
              role="tab"
              class="mc-accordion__header d-flex justify-content-between"
            >
              <span class="font-size-14">{{ $t('global.alert.dashcamAlert') }}</span>
              <i class="mc-fa icon-outline_chevron-down"></i>
            </div>
            <b-collapse id="accordion-2" role="tabpanel">
              <div class="mc-accordion__body">
                <b-skeleton-wrapper :loading="isLoading">
                  <template #loading>
                    <b-row class="my-3">
                      <b-col md="3">
                        <b-skeleton width="100%" height="50px" />
                      </b-col>
                      <b-col md="3">
                        <b-skeleton width="100%" height="50px" />
                      </b-col>
                      <b-col md="3">
                        <b-skeleton width="100%" height="50px" />
                      </b-col>
                      <b-col md="3">
                        <b-skeleton width="100%" height="50px" />
                      </b-col>
                    </b-row>
                    <b-row class="my-3">
                      <b-col md="3">
                        <b-skeleton width="100%" height="50px" />
                      </b-col>
                      <b-col md="3">
                        <b-skeleton width="100%" height="50px" />
                      </b-col>
                    </b-row>
                  </template>
                  <b-row>
                    <b-col md="3">
                      <FormulateInput
                        type="text"
                        name="costSmoking"
                        suffix
                        :disabled="
                          !hasAccessAll([
                            'raw_alerts_dms:read',
                            'alert_code:read',
                            'b3d-pelacakan-video-new:read',
                            'vehicle_video_trackers:read',
                          ])
                        "
                        :label="$t('global.alert.smoking')"
                        :placeholder="`${$t('global.form.enter')} ${$t('global.alert.smoking')}`"
                        validation-behavior="blur"
                        error-behavior="blur"
                        :validation="[
                          ['required'],
                          ['number'],
                          ['min', form.costSmoking],
                          ['min', 0],
                          ['max', form.costSmoking],
                          ['max', 100],
                        ]"
                        :validation-messages="{
                          min:
                            `${$t('global.alert.smoking')} ${$t('global.validation.min')}` + ' 0',
                          max:
                            `${$t('global.alert.smoking')} ${$t('global.validation.max')}` + ' 100',
                        }"
                      >
                        <template #suffix>
                          <div class="formulate-input-suffix">
                            <span>%</span>
                          </div>
                        </template>
                      </FormulateInput>
                    </b-col>
                    <b-col md="3">
                      <FormulateInput
                        suffix
                        type="text"
                        :disabled="
                          !hasAccessAll([
                            'raw_alerts_dms:read',
                            'alert_code:read',
                            'b3d-pelacakan-video-new:read',
                            'vehicle_video_trackers:read',
                          ])
                        "
                        name="costYawn"
                        :label="$t('global.alert.yawning')"
                        :placeholder="`${$t('global.form.enter')} ${$t('global.alert.yawning')}`"
                        validation-behavior="blur"
                        error-behavior="blur"
                        :validation="[
                          ['required'],
                          ['number'],
                          ['min', form.costYawn],
                          ['min', 0],
                          ['max', form.costYawn],
                          ['max', 100],
                        ]"
                        :validation-messages="{
                          min:
                            `${$t('global.alert.yawning')} ${$t('global.validation.min')}` + ' 0',
                          max:
                            `${$t('global.alert.yawning')} ${$t('global.validation.max')}` + ' 100',
                        }"
                      >
                        <template #suffix>
                          <div class="formulate-input-suffix">
                            <span>%</span>
                          </div>
                        </template>
                      </FormulateInput>
                    </b-col>
                    <b-col md="3">
                      <FormulateInput
                        type="text"
                        name="costEyeClosed"
                        :disabled="
                          !hasAccessAll([
                            'raw_alerts_dms:read',
                            'alert_code:read',
                            'b3d-pelacakan-video-new:read',
                            'vehicle_video_trackers:read',
                          ])
                        "
                        :label="$t('global.alert.asleep')"
                        suffix
                        :placeholder="`${$t('global.form.enter')} ${$t('global.alert.asleep')}`"
                        validation-behavior="blur"
                        error-behavior="blur"
                        :validation="[
                          ['required'],
                          ['number'],
                          ['min', form.costEyeClosed],
                          ['min', 0],
                          ['max', form.costEyeClosed],
                          ['max', 100],
                        ]"
                        :validation-messages="{
                          min: `${$t('global.alert.asleep')} ${$t('global.validation.min')}` + ' 0',
                          max:
                            `${$t('global.alert.asleep')} ${$t('global.validation.max')}` + ' 100',
                        }"
                      >
                        <template #suffix>
                          <div class="formulate-input-suffix">
                            <span>%</span>
                          </div>
                        </template>
                      </FormulateInput>
                    </b-col>
                    <b-col md="3">
                      <FormulateInput
                        type="text"
                        :disabled="
                          !hasAccessAll([
                            'raw_alerts_dms:read',
                            'alert_code:read',
                            'b3d-pelacakan-video-new:read',
                            'vehicle_video_trackers:read',
                          ])
                        "
                        name="costPhoneCalling"
                        suffix
                        :label="$t('global.alert.calling')"
                        :placeholder="`${$t('global.form.enter')} ${$t('global.alert.calling')}`"
                        validation-behavior="blur"
                        error-behavior="blur"
                        :validation="[
                          ['required'],
                          ['number'],
                          ['min', form.costPhoneCalling],
                          ['min', 0],
                          ['max', form.costPhoneCalling],
                          ['max', 100],
                        ]"
                        :validation-messages="{
                          min:
                            `${$t('global.alert.calling')} ${$t('global.validation.min')}` + ' 0',
                          max:
                            `${$t('global.alert.calling')} ${$t('global.validation.max')}` + ' 100',
                        }"
                      >
                        <template #suffix>
                          <div class="formulate-input-suffix">
                            <span>%</span>
                          </div>
                        </template>
                      </FormulateInput>
                    </b-col>
                    <b-col md="3" class="mt-2">
                      <FormulateInput
                        :disabled="
                          !hasAccessAll([
                            'raw_alerts_dms:read',
                            'alert_code:read',
                            'b3d-pelacakan-video-new:read',
                            'vehicle_video_trackers:read',
                          ])
                        "
                        type="text"
                        name="costDistraction"
                        :label="$t('global.alert.disturbance')"
                        suffix
                        :placeholder="`${$t('global.form.enter')} ${$t(
                          'global.alert.disturbance'
                        )}`"
                        validation-behavior="blur"
                        error-behavior="blur"
                        :validation="[
                          ['required'],
                          ['number'],
                          ['min', form.costDistraction],
                          ['min', 0],
                          ['max', form.costDistraction],
                          ['max', 100],
                        ]"
                        :validation-messages="{
                          min:
                            `${$t('global.alert.disturbance')} ${$t('global.validation.min')}` +
                            ' 0',
                          max:
                            `${$t('global.alert.disturbance')} ${$t('global.validation.max')}` +
                            ' 100',
                        }"
                      >
                        <template #suffix>
                          <div class="formulate-input-suffix">
                            <span>%</span>
                          </div>
                        </template>
                      </FormulateInput>
                    </b-col>
                    <b-col md="3" class="mt-2">
                      <FormulateInput
                        :disabled="
                          !hasAccessAll([
                            'raw_alerts_dms:read',
                            'alert_code:read',
                            'b3d-pelacakan-video-new:read',
                            'vehicle_video_trackers:read',
                          ])
                        "
                        type="text"
                        name="costMissingFace"
                        :label="$t('global.alert.outOfFrame')"
                        suffix
                        :placeholder="`${$t('global.form.enter')} ${$t('global.alert.outOfFrame')}`"
                        validation-behavior="blur"
                        error-behavior="blur"
                        :validation="[
                          ['required'],
                          ['number'],
                          ['min', form.costMissingFace],
                          ['min', 0],
                          ['max', form.costMissingFace],
                          ['max', 100],
                        ]"
                        :validation-messages="{
                          min:
                            `${$t('global.alert.outOfFrame')} ${$t('global.validation.min')}` +
                            ' 0',
                          max:
                            `${$t('global.alert.outOfFrame')} ${$t('global.validation.max')}` +
                            ' 100',
                        }"
                      >
                        <template #suffix>
                          <div class="formulate-input-suffix">
                            <span>%</span>
                          </div>
                        </template>
                      </FormulateInput>
                    </b-col>
                  </b-row>
                </b-skeleton-wrapper>
                <b-alert
                  :show="!hasAccessAll(['vehicle_video_trackers:read'])"
                  variant="primary"
                  class="mc-alert mt-3 w-100"
                >
                  <i class="mc-fa icon-fill_info"></i>
                  <span class="mc-alert__text">{{ $t('driverScore.tooltip.dmsConfig') }}</span>
                </b-alert>
              </div>
            </b-collapse>
          </div>
        </div>
      </FormulateForm>
    </b-card>
    <h4 class="card-title">
      {{ $t('driverScore.field.indexScore') }}
    </h4>
    <b-skeleton-wrapper :loading="isLoading">
      <template #loading>
        <b-card>
          <b-col sm="12" md="12" class="my-3">
            <b-skeleton width="100%" height="300px" />
          </b-col>
        </b-card>
      </template>

      <b-card v-if="scoreGrade">
        <b-col sm="12" md="12">
          <div class="row">
            <div class="table-responsive my-3 border border-1">
              <b-table
                small
                show-empty
                :fields="indexField"
                :items="scoreGrade"
                class="datatables"
                responsive="sm"
                empty-table
                :empty-text="$t('global.emptyTable')"
              >
                <template #cell(range)="data">
                  {{ data.item.minimum }} - {{ data.item.maximum }}
                </template>
              </b-table>
            </div>
          </div>
        </b-col>
      </b-card>
    </b-skeleton-wrapper>
    <b-modal id="cancel-update" size="md" :title="$t('global.cancel')" centered>
      <p>{{ $t('driverScore.text.cancelSetting') }}</p>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton variant="secondary" class="me-2" :text="$t('global.no')" @click="cancel()" />
          <McButton variant="primary" :text="$t('global.yes')" @click="cancelUpdate" />
        </div>
      </template>
    </b-modal>
    <b-modal id="back-confirm" size="md" :title="$t('global.alert.text.confirm')" centered>
      <p>{{ $t('driverScore.text.backSetting') }}</p>
      <template #modal-footer="{ cancel }">
        <div class="d-flex">
          <McButton
            variant="secondary"
            class="me-2"
            :text="$t('global.confirmation.exitPage')"
            @click="$router.push({ name: 'List Driver Score V2' })"
          />
          <McButton
            variant="primary"
            :text="$t('global.confirmation.stayPage')"
            @click="cancel()"
          />
        </div>
      </template>
    </b-modal>
  </Layout>
</template>
<script>
import Layout from '@/router/layouts/main'
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import McButton from '@/components/McButton'
import { Helper } from '@/mixin/helper'
import { MixPanel } from '@/mixin/mixpanel'
import { mapGetters } from 'vuex'
import {
  DRIVER_SCORE_SETTING,
  UPDATE_DRIVER_SCORE_SETTING,
} from '@/services/api/driverscore/settings'
import moment from 'moment'
moment.locale('en')

export default {
  page: {
    title: 'Konfigurasi',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
  },
  mixins: [Helper, MixPanel],

  data() {
    return {
      form: {
        minDistance: 300,
        percentageThreshold: 5,
        costHarshAccel: null,
        costHarshBrake: null,
        costSharpturn: null,
        costOverspeed: null,
        costIdleExceeded: null,
        costParkExceeded: null,
        costSmoking: null,
        costYawn: null,
        costEyeClosed: null,
        costPhoneCalling: null,
        costDistraction: null,
        costMissingFace: null,
      },
      oldData: {},
      scoreGrade: [],
      isSubmit: false,
      isDisable: true,
      isLoading: false,
    }
  },

  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
    }),
    title() {
      return this.$t('driverScore.title.driverScoreSetting')
    },
    indexField() {
      return [
        {
          key: 'grade',
          label: this.$t('global.object.index'),
          sortable: true,
          type: 'data',
        },
        {
          key: 'range',
          label: this.$t('global.object.limit'),
          sortable: true,
        },
      ]
    },
    hasChanged() {
      if (this.isLoading) return false
      const res = this.filterUnchangedData(this.form, this.oldData)
      return res[1]
    },
    totalPercentage() {
      let total = 0
      if (this.hasAccessAll(['vehicle_video_trackers:read'])) {
        total =
          Number(this.form.costHarshAccel) +
          Number(this.form.costHarshBrake) +
          Number(this.form.costSharpturn) +
          Number(this.form.costOverspeed) +
          Number(this.form.costIdleExceeded) +
          Number(this.form.costParkExceeded) +
          Number(this.form.costSmoking) +
          Number(this.form.costEyeClosed) +
          Number(this.form.costDistraction) +
          Number(this.form.costPhoneCalling) +
          Number(this.form.costMissingFace) +
          Number(this.form.costYawn)
      } else {
        total =
          Number(this.form.costHarshAccel) +
          Number(this.form.costHarshBrake) +
          Number(this.form.costSharpturn) +
          Number(this.form.costOverspeed) +
          Number(this.form.costIdleExceeded) +
          Number(this.form.costParkExceeded)
      }

      return total
    },
  },

  watch: {
    form: {
      deep: true,
      handler(newForm) {
        Object.keys(newForm).forEach((key) => {
          if (key.startsWith('cost') && newForm[key]) {
            this.form[key] = String(newForm[key]).replace(/[^0-9]/g, '')
          }
        })
      },
    },
  },

  async mounted() {
    await this.getData()
  },

  methods: {
    async getData() {
      this.isLoading = true
      const res = await this.$apollo.query({
        query: DRIVER_SCORE_SETTING,
        fetchPolicy: 'no-cache',
      })
      const driverScoreSetting = res.data.driverScoreSetting.driverScoreSetting
      this.form = {
        minDistance: driverScoreSetting.minDistance,
        percentageThreshold: driverScoreSetting.percentageThreshold,
        costHarshAccel: driverScoreSetting.costHarshAccel,
        costHarshBrake: driverScoreSetting.costHarshBrake,
        costSharpturn: driverScoreSetting.costSharpturn,
        costOverspeed: driverScoreSetting.costOverspeed,
        costIdleExceeded: driverScoreSetting.costIdleExceeded,
        costParkExceeded: driverScoreSetting.costParkExceeded,
        costSmoking: driverScoreSetting.costSmoking,
        costYawn: driverScoreSetting.costYawn,
        costEyeClosed: driverScoreSetting.costEyeClosed,
        costPhoneCalling: driverScoreSetting.costPhoneCalling,
        costDistraction: driverScoreSetting.costDistraction,
        costMissingFace: driverScoreSetting.costMissingFace,
      }
      this.oldData = this.form
      this.scoreGrade = res.data.driverScoreSetting.driverScoreSetting.scoreGrade
      setTimeout(() => {
        this.isLoading = false
      }, 500)
    },
    async updateData() {
      this.isSubmit = true
      if (this.totalPercentage != 100) {
        this.$mcToast.error(this.$t('driverScore.text.failedUpdateConfig'))
        this.isSubmit = false
        return
      }

      let dashCam = {}
      if (this.hasAccessAll(['vehicle_video_trackers:read'])) {
        dashCam = {
          costMissingFace: parseInt(this.form.costMissingFace),
          costDistraction: parseInt(this.form.costDistraction),
          costPhoneCalling: parseInt(this.form.costPhoneCalling),
          costEyeClosed: parseInt(this.form.costEyeClosed),
          costSmoking: parseInt(this.form.costSmoking),
          costYawn: parseInt(this.form.costYawn),
        }
      }

      const input = {
        percentageThreshold: parseInt(this.form.percentageThreshold),
        minDistance: parseInt(this.form.minDistance),
        costIdleExceeded: parseInt(this.form.costIdleExceeded),
        costParkExceeded: parseInt(this.form.costParkExceeded),
        costSharpturn: parseInt(this.form.costSharpturn),
        costOverspeed: parseInt(this.form.costOverspeed),
        costHarshAccel: parseInt(this.form.costHarshAccel),
        costHarshBrake: parseInt(this.form.costHarshBrake),
        ...dashCam,
      }

      const res = await this.$apollo.mutate({
        mutation: UPDATE_DRIVER_SCORE_SETTING,
        variables: {
          input,
        },
      })

      if (!res.data.updateDriverScoreSetting.isSuccessful) {
        this.$mcToast.error(this.$t('driverScore.text.failedUpdateConfig'))
        this.isSubmit = false
        return
      }
      this.$mcToast.success(this.$t('global.alert.text.changed'))
      await this.getData()
      this.track('Save Driver Score Configuration')
      this.isSubmit = false
    },
    resetConfig() {
      if (this.hasAccessAll(['vehicle_video_trackers:read'])) {
        this.form = {
          minDistance: 300,
          percentageThreshold: 5,
          costHarshAccel: 15,
          costHarshBrake: 15,
          costSharpturn: 15,
          costOverspeed: 10,
          costIdleExceeded: 5,
          costParkExceeded: 5,
          costSmoking: 15,
          costYawn: 0,
          costEyeClosed: 10,
          costPhoneCalling: 5,
          costDistraction: 5,
          costMissingFace: 0,
        }
      } else {
        this.form = {
          minDistance: 300,
          percentageThreshold: 5,
          costHarshAccel: 20,
          costHarshBrake: 25,
          costSharpturn: 20,
          costOverspeed: 15,
          costIdleExceeded: 10,
          costParkExceeded: 10,
          costSmoking: 0,
          costYawn: 0,
          costEyeClosed: 0,
          costPhoneCalling: 0,
          costDistraction: 0,
          costMissingFace: 0,
        }
      }
    },

    filterUnchangedData(newValue, oldValue) {
      const keys = new Set([...Object.keys(newValue), ...Object.keys(oldValue)])
      const _newValue = {}
      keys.forEach((key) => {
        if (oldValue[key] == newValue[key]) return
        _newValue[key] = newValue[key]
      })

      const entries = Object.entries(_newValue)

      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },

    backConfirmation() {
      if (this.hasChanged) {
        this.$bvModal.show('back-confirm')
      } else this.$router.push({ name: 'List Driver Score V2' })
    },

    cancelConfirmation() {
      this.$bvModal.show('cancel-update')
    },

    cancelUpdate() {
      this.form = this.oldData
      this.$bvModal.hide('cancel-update')
    },
  },
}
</script>
